import { format, isValid, parseISO } from "date-fns";

import { FilterDefinition } from "@/components/FilterBar";

import { OFFER_TYPES } from "../../constants";
import { formatNumber } from "../../utils";
import {
  deliveryCostAmount,
  demandDeliveryCost,
  latestDemandDeliveryCost,
  demandTermsOf,
  effectiveDeliveryCost,
  isDemandLine,
  type DeliveryCostValue,
} from "../../utils/delivery-cost";
import { isFilterSet, toFilterString } from "../../utils/filters";
import { UserGroup } from "../../utils/roles";
import { NegotiationProposal } from "./components/OfferCommentsModal/NegotiatedDetailsCard/offer-negotiation-proposal";
import {
  DeliveryConfirmation,
  DemandTerms,
  Offer,
  OfferStatus,
} from "./components/OfferCard/interfaces";
import {
  DeliveryLocationLabels,
  DeliveryLookups,
  OfferBucket,
  OfferFilterKey,
  OfferFilterValues,
  OfferListParams,
  OfferTab,
} from "./types";

export const OFFERS_PAGE_SIZE = 10;

// Latest offer status updates first ("Showing latest offer status updates first").
export const OFFERS_DEFAULT_ORDERING = "-last_update_datetime";

export const DEFAULT_OFFER_FILTERS: OfferFilterValues = {
  status: "",
  commodity_id: "",
  quality: "",
  aggregator_ids: "",
  tab: "all",
};

export const OFFER_BUCKETS: OfferBucket[] = [
  "AGGREGATOR_ACTION",
  "BUYER_ACTION",
  "IN_PROGRESS",
  "CLOSED",
];

const OFFER_TAB_BUCKETS: Record<Exclude<OfferTab, "all">, OfferBucket[]> = {
  aggregatorAction: ["AGGREGATOR_ACTION"],
  buyerAction: ["BUYER_ACTION"],
  inProgress: ["IN_PROGRESS"],
  needsAction: ["BUYER_ACTION"],
  // the buyer waits on the aggregator, or on the delivery
  awaiting: ["AGGREGATOR_ACTION", "IN_PROGRESS"],
  closed: ["CLOSED"],
};

/** The buckets a tab lists: the API puts every offer in exactly one bucket. */
export const tabBuckets = (tab: OfferTab): OfferBucket[] =>
  tab === "all" ? OFFER_BUCKETS : OFFER_TAB_BUCKETS[tab];

// The statuses of the offers in each bucket, for the Status filter of a tab: the delivery
// confirmation overlay puts a delivery being confirmed in the bucket of the side to act.
const BUCKET_STATUSES: Record<OfferBucket, OfferStatus[]> = {
  AGGREGATOR_ACTION: [
    OfferStatus.New,
    OfferStatus.Negotiating,
    OfferStatus.ExchangeDetailsDisputed,
    OfferStatus.ExchangeRejected,
    OfferStatus.OfferDone,
    OfferStatus.ExchangePaymentPending,
    OfferStatus.ExchangeDone,
  ],
  BUYER_ACTION: [
    OfferStatus.Negotiating,
    OfferStatus.ExchangeDetailsUpdated,
    OfferStatus.ExchangeRejectionDisputed,
    OfferStatus.ExchangePaymentPending,
    OfferStatus.OfferDone,
    OfferStatus.ExchangeDone,
  ],
  IN_PROGRESS: [OfferStatus.Accepted, OfferStatus.OfferDone, OfferStatus.ExchangeDetailsConfirmed],
  CLOSED: [
    OfferStatus.ExchangeDone,
    OfferStatus.Rejected,
    OfferStatus.Recalled,
    OfferStatus.ExchangeCanceled,
  ],
};

export const tabStatuses = (tab: OfferTab): OfferStatus[] => [
  ...new Set(tabBuckets(tab).flatMap(bucket => BUCKET_STATUSES[bucket])),
];

const BUYER_TURN_CONFIRMATIONS = [
  DeliveryConfirmation.AwaitingBuyer,
  DeliveryConfirmation.DisputedByAggregator,
];

const AGGREGATOR_TURN_CONFIRMATIONS = [
  DeliveryConfirmation.AwaitingAggregator,
  DeliveryConfirmation.DisputedByBuyer,
];

/**
 * Seeds the filter bar from router state, e.g. a status pushed by the dashboard quick filters:
 * it opens on the All tab, so the list holds every offer in that status, as the dashboard counts.
 */
export const buildInitialFilters = (state: unknown): OfferFilterValues => {
  const s = (state ?? {}) as Partial<Record<keyof OfferFilterValues, unknown>>;
  const legacy = (state ?? {}) as { aggregator_id?: unknown };
  return {
    status: toFilterString(s.status),
    commodity_id: toFilterString(s.commodity_id),
    quality: toFilterString(s.quality),
    aggregator_ids: toFilterString(s.aggregator_ids ?? legacy.aggregator_id),
    tab: "all",
  };
};

export const buildOfferParams = (
  filters: OfferFilterValues,
  page: number,
  traderId?: string
): OfferListParams => ({
  page,
  page_size: OFFERS_PAGE_SIZE,
  ordering: OFFERS_DEFAULT_ORDERING,
  ...(traderId ? { trader_id: traderId } : {}),
  with_exchange: true,
  with_location: true,
  with_original_terms: true,
  ...(filters.tab !== "all" ? { bucket: tabBuckets(filters.tab).join(",") } : {}),
  ...(isFilterSet(filters.status) ? { status: filters.status } : {}),
  ...(isFilterSet(filters.commodity_id) ? { buy_commodity_id: filters.commodity_id } : {}),
  ...(isFilterSet(filters.quality) ? { quality: filters.quality } : {}),
  ...(isFilterSet(filters.aggregator_ids) ? { aggregator_ids: filters.aggregator_ids } : {}),
});

/** "Status: Accepted, Commodity: Maize" for the filters currently narrowing the list. */
export const describeActiveFilters = (
  filters: OfferFilterValues,
  definitions: FilterDefinition<OfferFilterKey>[]
): string[] =>
  definitions
    .filter(definition => isFilterSet(filters[definition.key]))
    .map(definition => {
      const value = filters[definition.key];
      const option = definition.options.find(entry => entry.value === value);
      return `${definition.label}: ${definition.formatValue?.(value) ?? option?.label ?? value}`;
    });

export const formatOfferDateTime = (date?: string | null): string => {
  if (!date) {
    return "";
  }
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, "d MMM yyyy, HH:mm") : "";
};

/**
 * Orders comments/proposals oldest-first. Clones first: sort() mutates in
 * place, and the source is usually live redux state — mutating it trips the
 * store's immutability check and corrupts index order between dispatches.
 */
export const sortByCreationTime = <T extends { backend_creation_time: string }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    new Date(a.backend_creation_time) > new Date(b.backend_creation_time) ? 1 : -1
  );

/** The proposal that supersedes the offer's own terms, if negotiations are live. */
export const getActiveProposal = (offer: Offer): NegotiationProposal | undefined =>
  offer.status === OfferStatus.Negotiating || offer.status === OfferStatus.ExchangeDetailsUpdated
    ? offer.latest_negotiation_proposal
    : undefined;

export const offerPictureUrls = (offer: Offer): string[] => [
  ...new Set(
    offer.deposit?.picture_urls ??
      (offer.orders ?? []).flatMap(order => order.deposit?.picture_urls ?? [])
  ),
];

export const demandPreviewPhotos = (offers: Offer[]) => {
  const lines = offers.map(offerPictureUrls).filter(urls => urls.length);
  return { urls: lines[0] ?? [], moreLines: Math.max(lines.length - 1, 0) };
};

export const isProposalFromTrader = (entity?: { user?: { roles?: string[] } }): boolean =>
  !!entity?.user?.roles?.includes(UserGroup.TRADER_REPR);

/** The organisation behind a proposal's author, for naming a negotiation column. */
export const proposalOrganisationName = (offer: Offer, proposal: NegotiationProposal): string =>
  isProposalFromTrader(proposal) ? offer.trader?.name : offer.tagged_aggregator?.name;

export type OfferBadgeVariant = "default" | "destructive" | "warning" | "success";

export const statusBadgeVariant = (status: OfferStatus): OfferBadgeVariant => {
  switch (status) {
    case OfferStatus.Accepted:
    case OfferStatus.ExchangeAgreed:
    case OfferStatus.ExchangeDone:
      return "success";
    case OfferStatus.Recalled:
    case OfferStatus.Rejected:
    case OfferStatus.ExchangeRejected:
    case OfferStatus.ExchangeCanceled:
      return "destructive";
    case OfferStatus.OfferDone:
    case OfferStatus.ExchangeDetailsUpdated:
    case OfferStatus.ExchangeDetailsDisputed:
    case OfferStatus.ExchangeDetailsConfirmed:
    case OfferStatus.ExchangeRejectionDisputed:
    case OfferStatus.ExchangePaymentPending:
      return "warning";
    default:
      return "default";
  }
};

export const offerStatusBadgeVariant = (offer: Offer): OfferBadgeVariant =>
  // Waiting on us only when the aggregator made the last move.
  offer.status === OfferStatus.Negotiating
    ? isProposalFromTrader(offer.latest_negotiation_proposal)
      ? "default"
      : "warning"
    : statusBadgeVariant(offer.status);

const DELIVERY_STAGE_STATUSES = [
  OfferStatus.ExchangeDetailsUpdated,
  OfferStatus.ExchangeDetailsDisputed,
  OfferStatus.ExchangeDone,
  OfferStatus.ExchangePaymentPending,
];

/** The final round carries delivery terms, not a price counter, once the exchange stage begins. */
export const isDeliveryRound = (offer: Offer, round: number, total: number): boolean =>
  round === total - 1 &&
  (DELIVERY_STAGE_STATUSES.includes(offer.status) ||
    !!offer.exchange ||
    !!offer.delivery_recorded_by);

export const deliveredQuantity = (offer: Offer): number => {
  const recorded = Number(offer.delivered_quantity);
  if (offer.delivered_quantity && recorded > 0) {
    return recorded;
  }
  const line = Number(offer.exchange?.commodity_exchanges?.[0]?.quantity);
  if (line > 0) {
    return line;
  }
  const total = Number(offer.exchange?.actual_total_quantity);
  return total > 0 ? total : Number(offer.minimum_quantity);
};

const recordedUnitPrice = (offer: Offer): number => {
  const quantity = Number(offer.delivered_quantity);
  if (!offer.final_payment_amount || !(quantity > 0)) {
    return Number(offer.price);
  }
  const deliveryCost = offer.includes_delivery_cost
    ? deliveryCostAmount(offer.demand_terms?.delivery_cost)
    : 0;
  return (Number(offer.final_payment_amount) - deliveryCost) / quantity;
};

/**
 * The recorded exchange as a final round, so the delivered terms compare against the last agreed ones.
 * The app writes every delivered stock's line with the whole delivered quantity and the goods amount
 * paid (not a unit price), so one line gives the round. A demand line's round carries the demand's
 * delivery cost only when that line's payment covered it.
 */
export const exchangeRound = (
  offer: Offer,
  proposals: NegotiationProposal[]
): NegotiationProposal | undefined => {
  const exchange = offer.exchange;
  if (!exchange && !offer.delivery_recorded_by) {
    return undefined;
  }

  const line = exchange?.commodity_exchanges?.[0];
  const lineQuantity = Number(line?.quantity);
  const fromLine = !!line && lineQuantity > 0;
  const quantity = deliveredQuantity(offer);
  const price = fromLine ? Number(line.price) / lineQuantity : recordedUnitPrice(offer);
  const confirmedAt = exchange?.confirmed_at ?? offer.delivery_recorded_at ?? "";
  const recordedByBuyer = !exchange && offer.delivery_recorded_by === "BUYER";
  const last = proposals[proposals.length - 1];
  const demandLine = isDemandLine(offer);
  const carriesDemandCost = demandLine && !!offer.includes_delivery_cost;

  return {
    uuid: "exchange",
    user: {
      id: 0,
      first_name: "",
      last_name: "",
      roles: recordedByBuyer ? [UserGroup.TRADER_REPR] : [],
    },
    proposer: recordedByBuyer ? "trader" : "aggregator",
    proposal_type: "EXCHANGE",
    text: "",
    responded: true,
    trader_market_offer: offer.id,
    minimum_quantity: quantity,
    maximum_quantity: quantity,
    price,
    delivery_cost: demandLine
      ? carriesDemandCost
        ? (offer.demand_terms?.delivery_cost ?? null)
        : null
      : last
        ? effectiveDeliveryCost(last, offer)
        : offer.delivery_cost,
    ...(demandLine ? { includes_delivery_cost: carriesDemandCost } : {}),
    planned_exchange_time: confirmedAt,
    backend_creation_time: confirmedAt,
    frontend_creation_time: confirmedAt,
    delivery_location: last ? last.delivery_location : offer.delivery_location,
    custom_delivery_location: last
      ? last.custom_delivery_location
      : offer.custom_delivery_location_details?.id,
    custom_delivery_location_type: last
      ? last.custom_delivery_location_type
      : offer.custom_delivery_location_type,
  };
};

export const offerDeliveryNoteUrl = (offer: Offer): string | null =>
  offer.delivery_note ?? offer.exchange?.delivery_note ?? null;

/** A direct offer is raised against a stock deposit, so the offer itself is round #1 against it. */
export const hasDepositRound = (offer: Offer): boolean =>
  offer.offer_type === OFFER_TYPES.DIRECT_OFFER && !!offer.deposit?.price;

export const isBuyerDeliveryTurn = (offer: Offer): boolean =>
  !!offer.delivery_confirmation && BUYER_TURN_CONFIRMATIONS.includes(offer.delivery_confirmation);

export const isAggregatorDeliveryTurn = (offer: Offer): boolean =>
  !!offer.delivery_confirmation &&
  AGGREGATOR_TURN_CONFIRMATIONS.includes(offer.delivery_confirmation);

export const offerAwaitingParty = (offer: Offer): "aggregator" | "you" | null => {
  if (isBuyerDeliveryTurn(offer)) {
    return "you";
  }
  if (isAggregatorDeliveryTurn(offer)) {
    return "aggregator";
  }
  if (offer.status === OfferStatus.New) {
    return "aggregator";
  }
  if (offer.status !== OfferStatus.Negotiating) {
    return null;
  }
  return isProposalFromTrader(offer.latest_negotiation_proposal) ? "aggregator" : "you";
};

export const canRecallOffer = (status: OfferStatus): boolean =>
  [OfferStatus.New, OfferStatus.Accepted].includes(status);

/** The offer needs the representative to act, so its primary button is emphasised. */
export const offerNeedsResponse = (offer: Offer): boolean =>
  isBuyerDeliveryTurn(offer) ||
  offer.status === OfferStatus.ExchangeDetailsUpdated ||
  (offer.status === OfferStatus.Negotiating &&
    !isProposalFromTrader(offer.latest_negotiation_proposal));

export const offerStatusLabelKey = (status: OfferStatus): string => {
  const keys: Record<OfferStatus, string> = {
    [OfferStatus.Accepted]: "offerStatuses.accepted",
    [OfferStatus.ExchangeDone]: "offerStatuses.exchange_done",
    [OfferStatus.ExchangeAgreed]: "offerStatuses.exchange_agreed",
    [OfferStatus.New]: "offerStatuses.new",
    [OfferStatus.Recalled]: "offerStatuses.recalled",
    [OfferStatus.Rejected]: "offerStatuses.rejected",
    [OfferStatus.OfferDone]: "offerStatuses.offer_done",
    [OfferStatus.Negotiating]: "offerStatuses.negotiating",
    [OfferStatus.ExchangeDetailsUpdated]: "offerStatuses.exchange_details_updated",
    [OfferStatus.ExchangeDetailsDisputed]: "offerStatuses.exchange_details_disputed",
    [OfferStatus.ExchangeDetailsConfirmed]: "offerStatuses.exchange_details_confirmed",
    [OfferStatus.ExchangeRejected]: "offerStatuses.exchange_rejected",
    [OfferStatus.ExchangeRejectionDisputed]: "offerStatuses.exchange_rejection_disputed",
    [OfferStatus.ExchangePaymentPending]: "offerStatuses.exchange_payment_pending",
    [OfferStatus.ExchangeCanceled]: "offerStatuses.exchange_canceled",
  };
  return keys[status] ?? "offerStatuses.new";
};

export interface OfferDisplayStatus {
  labelKey: string;
  params?: Record<string, string>;
  variant: OfferBadgeVariant;
}

export const offerDisplayStatus = (offer: Offer, readOnly = false): OfferDisplayStatus => {
  if (offer.status === OfferStatus.ExchangeCanceled) {
    return {
      labelKey: offer.delivery_rejection
        ? "offerStatuses.delivery_rejected"
        : "offerStatuses.exchange_canceled",
      variant: "destructive",
    };
  }
  switch (offer.delivery_confirmation) {
    case DeliveryConfirmation.AwaitingBuyer:
      return {
        labelKey: readOnly
          ? "offerStatuses.delivery_awaiting_buyer"
          : "offerStatuses.delivery_awaiting_you",
        variant: "warning",
      };
    case DeliveryConfirmation.AwaitingAggregator:
      return {
        labelKey: "offerStatuses.delivery_awaiting_aggregator",
        params: { aggregator: offer.tagged_aggregator?.name ?? "" },
        variant: "default",
      };
    case DeliveryConfirmation.DisputedByAggregator:
    case DeliveryConfirmation.DisputedByBuyer:
      return { labelKey: "offerStatuses.delivery_disputed", variant: "destructive" };
    case DeliveryConfirmation.Confirmed:
      return offer.status === OfferStatus.ExchangePaymentPending
        ? { labelKey: "offerStatuses.delivery_confirmed_payment_pending", variant: "warning" }
        : { labelKey: "offerStatuses.delivery_confirmed", variant: "success" };
    default:
      return {
        labelKey: offerStatusLabelKey(offer.status),
        variant: offerStatusBadgeVariant(offer),
      };
  }
};

export interface DeliveryActions {
  complete: boolean;
  correct: boolean;
  review: boolean;
  reject: boolean;
}

const NO_DELIVERY_ACTIONS: DeliveryActions = {
  complete: false,
  correct: false,
  review: false,
  reject: false,
};

const hasRecordedExchange = (offer: Offer): boolean =>
  !!offer.exchange?.commodity_exchanges?.length;

export const deliveryActionsFor = (offer: Offer, readOnly = false): DeliveryActions => {
  if (readOnly || offer.is_school_offer) {
    return NO_DELIVERY_ACTIONS;
  }
  const ready = offer.status === OfferStatus.OfferDone && !hasRecordedExchange(offer);
  const confirmation = offer.delivery_confirmation ?? null;
  return {
    complete: ready && confirmation === null,
    correct:
      ready &&
      (confirmation === DeliveryConfirmation.AwaitingAggregator ||
        confirmation === DeliveryConfirmation.DisputedByAggregator),
    review:
      (offer.status === OfferStatus.ExchangePaymentPending ||
        offer.status === OfferStatus.ExchangeDone) &&
      confirmation === DeliveryConfirmation.AwaitingBuyer,
    reject: ready,
  };
};

export const hasDeliveryActions = (actions: DeliveryActions): boolean =>
  actions.complete || actions.correct || actions.review || actions.reject;

const PROPOSED_TERM_STATUSES = [
  OfferStatus.New,
  OfferStatus.Negotiating,
  OfferStatus.Rejected,
  OfferStatus.Recalled,
];

/** Offer terms are only "agreed" once the aggregator has accepted them. */
export const hasProposedTerms = (status: OfferStatus): boolean =>
  PROPOSED_TERM_STATUSES.includes(status);

export const offerTermsHeadingKey = (status: OfferStatus): string =>
  hasProposedTerms(status)
    ? "marketOfferView.details.offerDetailsProposed"
    : "marketOfferView.details.offerDetailsAgreed";

// A list row is either a standalone offer (demand = null) or a demand group
// bundling the child offers that share a demand id.
export type OfferRow =
  | { type: "offer"; offer: Offer }
  | { type: "demand"; demandId: number; offers: Offer[] };

// Groups child offers under their demand while keeping the server's ordering:
// each demand appears where its first child does, standalone offers stay inline.
export const groupOffersByDemand = (offers: Offer[]): OfferRow[] => {
  const rows: OfferRow[] = [];
  const rowByDemand = new Map<number, { type: "demand"; demandId: number; offers: Offer[] }>();

  offers.forEach(offer => {
    const demandId = offer.demand ?? null;
    if (demandId === null) {
      rows.push({ type: "offer", offer });
      return;
    }
    const existing = rowByDemand.get(demandId);
    if (existing) {
      existing.offers.push(offer);
    } else {
      const row = { type: "demand" as const, demandId, offers: [offer] };
      rowByDemand.set(demandId, row);
      rows.push(row);
    }
  });

  return rows;
};

// A demand raised from a school bid request is flagged per child offer by the
// API (`is_school_offer`, derived from the demand's source bid request). All
// children of one demand share the origin, so `.every` and `.some` agree; the
// flag is authoritative now that school offers also carry a quality grade.
export const isSchoolDemand = (offers: Offer[]): boolean =>
  offers.length > 0 && offers.every(offer => !!offer.is_school_offer);

// Least-advanced non-rejected child drives the demand rollup, mirroring the
// backend compute_rollup_status: rejected children are non-fatal, and the demand
// is "done" only once every remaining child is done.
const DEMAND_STATUS_RANK: Record<string, number> = {
  [OfferStatus.New]: 0,
  [OfferStatus.Negotiating]: 1,
  [OfferStatus.ExchangeDetailsUpdated]: 1,
  [OfferStatus.ExchangeDetailsDisputed]: 1,
  [OfferStatus.Accepted]: 2,
  [OfferStatus.OfferDone]: 3,
  [OfferStatus.ExchangeAgreed]: 4,
  [OfferStatus.ExchangePaymentPending]: 5,
  [OfferStatus.ExchangeDone]: 6,
};

const rankOf = (status: OfferStatus): number => DEMAND_STATUS_RANK[status] ?? 0;

export interface DemandRollup {
  status: OfferStatus;
  total: number;
  rejected: number;
  partiallyAccepted: boolean;
}

const OUT_OF_ROLLUP_STATUSES = [OfferStatus.Rejected, OfferStatus.ExchangeCanceled];

export const computeDemandRollup = (offers: Offer[]): DemandRollup => {
  const total = offers.length;
  const rejected = offers.filter(offer => offer.status === OfferStatus.Rejected).length;
  const active = offers.filter(offer => !OUT_OF_ROLLUP_STATUSES.includes(offer.status));

  if (active.length === 0) {
    const status = offers.some(offer => offer.status === OfferStatus.ExchangeCanceled)
      ? OfferStatus.ExchangeCanceled
      : OfferStatus.Rejected;
    return { status, total, rejected, partiallyAccepted: false };
  }

  const status = active.reduce<OfferStatus>(
    (least, offer) => (rankOf(offer.status) < rankOf(least) ? offer.status : least),
    active[0].status
  );

  const anyAdvanced = active.some(offer => rankOf(offer.status) >= rankOf(OfferStatus.Accepted));
  const anyPending = active.some(
    offer => offer.status === OfferStatus.New || offer.status === OfferStatus.Negotiating
  );

  return { status, total, rejected, partiallyAccepted: anyAdvanced && anyPending };
};

export const demandStatusBadgeVariant = (status: OfferStatus): OfferBadgeVariant => {
  switch (status) {
    case OfferStatus.Accepted:
    case OfferStatus.ExchangeAgreed:
    case OfferStatus.ExchangeDone:
      return "success";
    case OfferStatus.Rejected:
    case OfferStatus.Recalled:
    case OfferStatus.ExchangeDetailsDisputed:
    case OfferStatus.ExchangeCanceled:
      return "destructive";
    case OfferStatus.OfferDone:
    case OfferStatus.ExchangeDetailsUpdated:
      return "warning";
    default:
      return "default";
  }
};

export const commentsButtonLabelKey = (offer: Offer): string => {
  if (offer.status === OfferStatus.OfferDone && offer.delivery_confirmation) {
    return "offerCard.deliveryDetails";
  }
  switch (offer.status) {
    case OfferStatus.Recalled:
    case OfferStatus.Rejected:
    case OfferStatus.ExchangeCanceled:
      return "offerCard.seeReasons";
    case OfferStatus.Negotiating:
      return isProposalFromTrader(offer.latest_negotiation_proposal)
        ? "offerCard.viewNegotiation"
        : "offerCard.viewCounterOffer";
    case OfferStatus.ExchangeDetailsUpdated:
      return "offerCard.reviewExchangeQuantity";
    case OfferStatus.ExchangeDone:
      return "offerCard.exchangeDetails";
    default:
      return "offerCard.viewOfferDetails";
  }
};

export const formatQuantityRange = (minimum: string | number, maximum: string | number): string =>
  Number(minimum) === Number(maximum)
    ? formatNumber(minimum)
    : `${formatNumber(minimum)} - ${formatNumber(maximum)}`;

export const formatUnitPrice = (price: string | number, currency: string, unit: string): string =>
  `${currency}${formatNumber(price)} / ${unit}`;

/** Recalled/declined offers come back with their price zeroed; fall back to the last proposal. */
export const lastKnownPrice = (offer: Offer): string | number =>
  Number(offer.price) ? offer.price : (offer.latest_negotiation_proposal?.price ?? offer.price);

export const formatTotalPrice = (
  terms: Pick<Offer, "price" | "minimum_quantity" | "maximum_quantity" | "delivery_cost">,
  currency: string
): string => {
  const price = Number(terms.price);
  const minimum = Number(terms.minimum_quantity);
  const maximum = Number(terms.maximum_quantity || terms.minimum_quantity);
  const deliveryCost = deliveryCostAmount(terms.delivery_cost);
  return `${currency}${formatQuantityRange(
    price * minimum + deliveryCost,
    price * maximum + deliveryCost
  )}`;
};

export type DeliveryTermsSource = Pick<NegotiationProposal, "delivery_location"> & {
  custom_delivery_location?: number | null;
  custom_delivery_location_type?: number | null;
};

/**
 * Resolves the address lines for an offer's delivery location. A live proposal
 * overrides the offer's own location. Labels are injected so this stays
 * i18n-free and testable.
 */
export const resolveDeliveryLocation = (
  offer: Offer,
  proposal: DeliveryTermsSource | null | undefined,
  lookups: DeliveryLookups,
  labels: DeliveryLocationLabels
): string[] => {
  const deliveryLocation = proposal ? proposal.delivery_location : offer.delivery_location;

  switch (deliveryLocation) {
    case "drop_at_trader": {
      const trader = lookups.traders.find(({ id }) => id === offer.trader?.id);
      return [
        trader?.name || labels.nameUnavailable,
        trader?.physical_address || labels.addressUnavailable,
      ];
    }
    case "pick_from_aggregator": {
      const aggregator = lookups.aggregators.find(({ id }) => id === offer.tagged_aggregator?.id);
      return [
        aggregator?.name || labels.nameUnavailable,
        aggregator?.physical_address || labels.addressUnavailable,
      ];
    }
    case "custom_exchange_location": {
      const locationId = proposal
        ? proposal.custom_delivery_location
        : offer.custom_delivery_location_details?.id;
      const typeId = proposal
        ? proposal.custom_delivery_location_type
        : offer.custom_delivery_location_type;

      const location = lookups.customExchangeLocations.find(({ id }) => id === Number(locationId));
      const locationType = lookups.exchangeLocationTypes.find(({ id }) => id === Number(typeId));
      const area = location
        ? lookups.administrativeAreas.find(({ id }) => id === Number(location.location))
        : undefined;

      return [location?.name || labels.nameUnavailable, locationType?.name, area?.name].filter(
        (line): line is string => !!line
      );
    }
    default:
      return [labels.unknownDeliveryType];
  }
};

/** Progress of deposits added against a market offer's maximum quantity. */
export const getOfferProgress = (offer: Offer): { completed: number; total: number } => {
  const total = (offer.orders || []).reduce(
    (sum, order) => sum + (Number(order.deposit?.quantity) || 0),
    0
  );
  const maximum = Number(offer.maximum_quantity);
  const completed = maximum > 0 ? Math.min(100, (total / maximum) * 100) : 0;
  return { completed, total };
};

export interface TermChange {
  direction: "up" | "down";
  absolute: number;
}

/** Change of a counter-offer term against the original offer's value. */
export const compareTerm = (original: number, counter: number): TermChange | null => {
  if (!Number.isFinite(original) || !Number.isFinite(counter) || original === counter) {
    return null;
  }
  const absolute = counter - original;
  return { direction: absolute > 0 ? "up" : "down", absolute };
};

export interface OfferTerms {
  price: number;
  minimumQuantity: number;
  maximumQuantity: number;
  deliveryCost: number;
}

const toOfferTerms = (source: {
  price: string | number;
  minimum_quantity: string | number;
  maximum_quantity: string | number;
  delivery_cost?: DeliveryCostValue;
}): OfferTerms => ({
  price: Number(source.price),
  minimumQuantity: Number(source.minimum_quantity),
  maximumQuantity: Number(source.maximum_quantity || source.minimum_quantity),
  deliveryCost: deliveryCostAmount(source.delivery_cost),
});

export const offerOriginalSource = (offer: Offer): Offer => {
  const original = offer.original_terms;
  if (!original) {
    return offer;
  }
  return {
    ...offer,
    ...original,
    planned_exchange_time: original.planned_exchange_time ?? undefined,
    custom_delivery_location: original.custom_delivery_location ?? undefined,
    custom_delivery_location_type: original.custom_delivery_location_type ?? undefined,
  };
};

/** The offer's own terms, before any live counter-proposal. */
export const offerBaselineTerms = (offer: Offer): OfferTerms =>
  toOfferTerms({ ...offer, price: lastKnownPrice(offer) });

export const activeProposalTerms = (offer: Offer): NegotiationProposal | undefined => {
  const proposal = getActiveProposal(offer);
  return proposal && { ...proposal, delivery_cost: effectiveDeliveryCost(proposal, offer) };
};

/** The terms in force: a live proposal's when there is one, the offer's own otherwise. */
export const offerTerms = (offer: Offer): OfferTerms => {
  const proposal = activeProposalTerms(offer);
  return proposal ? toOfferTerms(proposal) : offerBaselineTerms(offer);
};

export const termsValue = (terms: OfferTerms): number => terms.price * terms.minimumQuantity;

export const termsTotal = (terms: OfferTerms): number => termsValue(terms) + terms.deliveryCost;

export interface TermMove {
  from: number;
  to: number;
}

export interface TermMoves {
  quantity?: TermMove;
  price?: TermMove;
  deliveryCost?: TermMove;
  value?: TermMove;
}

const termMove = (from: number, to: number): TermMove | undefined =>
  Number.isFinite(from) && Number.isFinite(to) && from !== to ? { from, to } : undefined;

/**
 * What a live proposal moves against the offer's own terms. The list payload carries only the
 * latest proposal, so the offer itself is the baseline.
 */
export const offerTermChanges = (offer: Offer): TermMoves => {
  const baseline = offerBaselineTerms(offer);
  const current = offerTerms(offer);

  return {
    quantity: termMove(baseline.minimumQuantity, current.minimumQuantity),
    price: termMove(baseline.price, current.price),
    deliveryCost: termMove(baseline.deliveryCost, current.deliveryCost),
    value: termMove(termsTotal(baseline), termsTotal(current)),
  };
};

const BRIEF_PREFIX = "representativeOffers.card.brief";

const deliveryBriefKey = (offer: Offer): string | null => {
  if (offer.status === OfferStatus.ExchangeCanceled) {
    if (!offer.delivery_rejection) {
      return `${BRIEF_PREFIX}.exchangeCanceled`;
    }
    return offer.delivery_rejection.side === "BUYER"
      ? `${BRIEF_PREFIX}.deliveryRejectedByBuyer`
      : `${BRIEF_PREFIX}.deliveryRejectedByAggregator`;
  }
  switch (offer.delivery_confirmation) {
    case DeliveryConfirmation.AwaitingBuyer:
      return `${BRIEF_PREFIX}.deliveryAwaitingBuyer`;
    case DeliveryConfirmation.AwaitingAggregator:
      return `${BRIEF_PREFIX}.deliveryAwaitingAggregator`;
    case DeliveryConfirmation.DisputedByAggregator:
      return `${BRIEF_PREFIX}.deliveryDisputedByAggregator`;
    case DeliveryConfirmation.DisputedByBuyer:
      return `${BRIEF_PREFIX}.deliveryDisputedByBuyer`;
    case DeliveryConfirmation.Confirmed:
      return offer.status === OfferStatus.ExchangePaymentPending
        ? `${BRIEF_PREFIX}.deliveryConfirmedPaymentPending`
        : `${BRIEF_PREFIX}.deliveryConfirmed`;
    default:
      return null;
  }
};

/** One-line summary of what the offer's status means for the representative. */
export const offerBriefKey = (offer: Offer): string => {
  const deliveryBrief = deliveryBriefKey(offer);
  if (deliveryBrief) {
    return deliveryBrief;
  }
  switch (offer.status) {
    case OfferStatus.Negotiating: {
      if (isProposalFromTrader(offer.latest_negotiation_proposal)) {
        return `${BRIEF_PREFIX}.awaitingAggregator`;
      }
      const { quantity, price } = offerTermChanges(offer);
      if (quantity && price) {
        return `${BRIEF_PREFIX}.counteredBoth`;
      }
      if (quantity) {
        return `${BRIEF_PREFIX}.counteredQuantity`;
      }
      if (price) {
        return `${BRIEF_PREFIX}.counteredPrice`;
      }
      return `${BRIEF_PREFIX}.countered`;
    }
    case OfferStatus.Accepted:
      return `${BRIEF_PREFIX}.accepted`;
    case OfferStatus.OfferDone:
      return `${BRIEF_PREFIX}.readyForExchange`;
    case OfferStatus.ExchangeAgreed:
      return `${BRIEF_PREFIX}.exchangeAgreed`;
    case OfferStatus.ExchangeDetailsUpdated:
      return `${BRIEF_PREFIX}.exchangeDetailsUpdated`;
    case OfferStatus.ExchangeDetailsDisputed:
      return `${BRIEF_PREFIX}.exchangeDetailsDisputed`;
    case OfferStatus.ExchangePaymentPending:
      return `${BRIEF_PREFIX}.exchangePaymentPending`;
    case OfferStatus.ExchangeDone:
      return `${BRIEF_PREFIX}.exchangeDone`;
    case OfferStatus.Rejected:
      return `${BRIEF_PREFIX}.rejected`;
    case OfferStatus.Recalled:
      return `${BRIEF_PREFIX}.recalled`;
    default:
      return `${BRIEF_PREFIX}.new`;
  }
};

export interface DemandTotals {
  items: number;
  quantity: number;
  value: number;
  deliveryCost: number;
}

const activeOffers = (offers: Offer[]): Offer[] =>
  offers.filter(offer => !OUT_OF_ROLLUP_STATUSES.includes(offer.status));

const OUT_STATUSES = [OfferStatus.Rejected, OfferStatus.Recalled, OfferStatus.ExchangeCanceled];

/** A demand's lines still in play: declined and recalled lines are out. */
export const demandLinesInPlay = (offers: Offer[]): Offer[] =>
  offers.filter(offer => !OUT_STATUSES.includes(offer.status));

/**
 * Line count, quantity and goods value of a demand's lines in play, plus the demand's single
 * delivery cost, counted once (and dropped once no line is left in play).
 */
export const demandTotals = (offers: Offer[]): DemandTotals => {
  const totals = demandLinesInPlay(offers).reduce(
    (sum, offer) => {
      const terms = offerTerms(offer);
      return {
        items: sum.items + 1,
        quantity: sum.quantity + terms.minimumQuantity,
        value: sum.value + termsValue(terms),
      };
    },
    { items: 0, quantity: 0, value: 0 }
  );
  return {
    ...totals,
    deliveryCost: totals.items ? deliveryCostAmount(demandDeliveryCost(offers)) : 0,
  };
};

/** The demand's delivery cost as it stands (latest proposal) against the amount the buyer first offered. */
export const demandDeliveryCostMove = (terms: DemandTerms | null): TermMove | undefined =>
  terms
    ? termMove(
        deliveryCostAmount(terms.original_delivery_cost),
        deliveryCostAmount(latestDemandDeliveryCost(terms))
      )
    : undefined;

/** Demand-level moves: summed line baselines against summed live terms, plus the demand's cost. */
export const demandTermChanges = (offers: Offer[]): TermMoves => {
  const sum = (pick: (offer: Offer) => OfferTerms) =>
    demandLinesInPlay(offers).reduce(
      (totals, offer) => {
        const terms = pick(offer);
        return {
          quantity: totals.quantity + terms.minimumQuantity,
          value: totals.value + termsValue(terms),
        };
      },
      { quantity: 0, value: 0 }
    );

  const baseline = sum(offerBaselineTerms);
  const current = sum(offerTerms);
  const terms = demandTermsOf(offers);
  const originalCost = deliveryCostAmount(terms?.original_delivery_cost);
  const currentCost = deliveryCostAmount(latestDemandDeliveryCost(terms));

  return {
    quantity: termMove(baseline.quantity, current.quantity),
    deliveryCost: demandDeliveryCostMove(terms),
    value: termMove(baseline.value + originalCost, current.value + currentCost),
  };
};

const STANDING_STATUSES = [OfferStatus.New, OfferStatus.Negotiating];

/**
 * The aggregator's pending delivery-cost amount the buyer also accepts by closing this line: once a
 * demand's last standing line is closed, the backend locks the demand's cost on the aggregator's
 * pending proposal (when every line ends up declined or recalled, it is withdrawn instead).
 * Null when closing the line settles nothing.
 */
export const closingAcceptsDeliveryCost = (offer: Offer): DeliveryCostValue => {
  const terms = offer.demand_terms;
  const pending = terms?.pending_delivery_cost_proposal;
  if (
    !terms ||
    terms.delivery_cost_locked ||
    terms.standing_lines !== 1 ||
    !STANDING_STATUSES.includes(offer.status) ||
    pending?.side !== "AGGREGATOR"
  ) {
    return null;
  }
  return pending.delivery_cost;
};

/** The line a demand's single call-to-action acts on: the one needing a response, else the least advanced. */
export const rollupDriverOffer = (offers: Offer[]): Offer => {
  const pool = activeOffers(offers).length ? activeOffers(offers) : offers;
  return (
    pool.find(offerNeedsResponse) ??
    pool.reduce(
      (least, offer) => (rankOf(offer.status) < rankOf(least.status) ? offer : least),
      pool[0]
    )
  );
};

// Read-only on an offer PUT: the payment flag is the aggregator's, the demand summary is derived.
const READ_ONLY_OFFER_KEYS = [
  "includes_delivery_cost",
  "demand_terms",
  "delivery_confirmation",
  "delivery_recorded_by",
  "delivery_recorded_at",
  "delivered_quantity",
  "delivery_payment_method",
  "delivery_payment_method_details",
  "delivery_dispute_topics",
  "delivery_dispute_comment",
  "delivery_disputed_at",
  "delivery_confirmed_at",
  "delivery_rejection",
];

/** An offer read turned into a PUT body; a demand line's cost is the demand's, so it sends none. */
export const toOfferUpdatePayload = <T extends Offer>(offer: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(offer).filter(
      ([key]) =>
        !READ_ONLY_OFFER_KEYS.includes(key) && !(key === "delivery_cost" && isDemandLine(offer))
    )
  ) as Partial<T>;
